import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HoverHintWithGlyph, PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import { moduleNavBarItems, navItemsForApp, type NavItem } from "@/lib/appNavigation";
import { useNavigationTrail } from "@/lib/navigationTrailContext";
import { isIntegratorAdmin, useIntegratorMe } from "@/lib/me-query";
import { integratorModuleLabel } from "@hub-modules";
import { cn } from "@/lib/utils";
import { fetchModuleNavGroups, moduleNavQueryKey, type ModuleNavGroup } from "@/components/ModuleGroupedNav";

const ICONS = ["packages", "chart", "factory", "boxes", "shield", "clipboard", "cart", "people", "truck", "calendar", "tags", "wrench", "warehouse", "document", "radio", "calculator"];

interface ModuleHomeProps {
  moduleKey: string;
  title?: string;
  introduction?: string;
}

function isSettingsItem(item: NavItem): boolean {
  return item.to.endsWith("/settings") || item.label.trim().toLowerCase() === "settings";
}

export function ModuleHome({ moduleKey, title, introduction }: ModuleHomeProps) {
  const { resetNavigationTrail } = useNavigationTrail();
  const me = useIntegratorMe();
  const queryClient = useQueryClient();
  const pages = moduleNavBarItems(navItemsForApp(moduleKey)).filter((item) => !isSettingsItem(item));
  const groupsQuery = useQuery({
    queryKey: moduleNavQueryKey(moduleKey),
    queryFn: () => fetchModuleNavGroups(moduleKey),
    staleTime: 60_000,
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ModuleNavGroup[] | null>(null);
  const canEdit =
    isIntegratorAdmin(me.data?.user) || me.data?.user.integratorSubappAccess?.[moduleKey] === "admin";
  const stored = groupsQuery.data ?? [];
  const assigned = new Set(stored.flatMap((group) => group.pagePaths));
  const ungroupedPages = pages.filter((item) => !assigned.has(item.to));
  const shown = useMemo(() => {
    const source = draft ?? stored;
    if (source.length === 0 && ungroupedPages.length === 0) return [];
    const extra =
      ungroupedPages.length > 0 && !editing
        ? [{ id: "ungrouped", label: "Ungrouped", icon: "boxes", pagePaths: ungroupedPages.map((item) => item.to) }]
        : [];
    return [...source, ...extra];
  }, [draft, editing, stored, ungroupedPages]);

  const save = useMutation({
    mutationFn: (groups: ModuleNavGroup[]) =>
      apiFetch(`/api/module-nav/${moduleKey}`, {
        method: "PUT",
        body: JSON.stringify({
          groups: groups
            .filter((group) => group.id !== "ungrouped")
            .map((group) => ({ label: group.label, icon: group.icon, pagePaths: group.pagePaths })),
        }),
      }),
    onSuccess: async () => {
      setEditing(false);
      setDraft(null);
      await queryClient.invalidateQueries({ queryKey: moduleNavQueryKey(moduleKey) });
    },
  });

  function startEdit(): void {
    const next = stored.map((group) => ({ ...group, pagePaths: [...group.pagePaths] }));
    if (ungroupedPages.length > 0) {
      next.push({
        id: "ungrouped",
        label: "Ungrouped",
        icon: "boxes",
        pagePaths: ungroupedPages.map((item) => item.to),
      });
    }
    setDraft(next);
    setEditing(true);
  }

  function onDrop(pagePath: string, targetGroupId: string, beforePath?: string): void {
    setDraft((current) => {
      if (!current) return current;
      const next = current.map((group) => ({ ...group, pagePaths: group.pagePaths.filter((path) => path !== pagePath) }));
      const target = next.find((group) => group.id === targetGroupId);
      if (!target) return current;
      const index = beforePath ? target.pagePaths.indexOf(beforePath) : target.pagePaths.length;
      target.pagePaths.splice(index < 0 ? target.pagePaths.length : index, 0, pagePath);
      return next;
    });
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-3">
        <PageTitleWithHelp hoverHint={title ?? integratorModuleLabel(moduleKey)}>
          {title ?? integratorModuleLabel(moduleKey)}
        </PageTitleWithHelp>
        {canEdit ? (
          editing ? (
            <button
              type="button"
              className="rounded-md bg-brand-700 px-3 py-2 text-sm font-semibold text-white"
              onClick={() =>
                draft &&
                save.mutate(
                  draft.filter((group) => group.id !== "ungrouped" && group.label.trim().toLowerCase() !== "ungrouped"),
                )
              }
            >
              Done
            </button>
          ) : (
            <button type="button" className="rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold" onClick={startEdit}>
              Edit navigation
            </button>
          )
        ) : null}
      </header>
      {introduction ? <p className="max-w-3xl text-sm text-slate-600">{introduction}</p> : null}
      {shown.length === 0 ? (
        <p className="text-sm text-slate-600">This module has no sections to choose from yet.</p>
      ) : (
        shown.map((group) => (
          <section
            key={group.id}
            onDragOver={(event) => editing && event.preventDefault()}
            onDrop={(event) => {
              if (!editing) return;
              event.preventDefault();
              const pagePath = event.dataTransfer.getData("text/plain");
              if (pagePath) onDrop(pagePath, group.id);
            }}
          >
            <div className="mb-2 flex items-center gap-2">
              {editing && group.id !== "ungrouped" ? (
                <input
                  value={group.label}
                  onChange={(event) =>
                    setDraft((current) =>
                      current?.map((item) => (item.id === group.id ? { ...item, label: event.target.value } : item)) ?? current,
                    )
                  }
                  className="rounded-md border border-slate-300 px-2 py-1 text-sm font-semibold"
                />
              ) : (
                <h2 className="text-sm font-semibold text-slate-800">{group.label}</h2>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {group.pagePaths.map((pagePath) => {
                const item = pages.find((page) => page.to === pagePath);
                if (!item) return null;
                const chip = (
                  <Link
                    to={item.to}
                    draggable={editing}
                    onDragStart={(event) => event.dataTransfer.setData("text/plain", item.to)}
                    onDragOver={(event) => editing && event.preventDefault()}
                    onDrop={(event) => {
                      if (!editing) return;
                      event.preventDefault();
                      event.stopPropagation();
                      onDrop(event.dataTransfer.getData("text/plain"), group.id, item.to);
                    }}
                    onClick={editing ? (event) => event.preventDefault() : resetNavigationTrail}
                    className={cn(
                      "inline-flex h-9 items-center gap-2 rounded-full border border-slate-300 bg-white px-3 text-sm",
                      "max-md:h-12 max-md:px-4 max-md:text-base",
                    )}
                  >
                    {item.label}
                    {item.description ? <HoverHintWithGlyph hoverHint={item.description} size="sm" /> : null}
                  </Link>
                );
                return <span key={item.to}>{chip}</span>;
              })}
            </div>
            {editing && group.id !== "ungrouped" ? (
              <div className="mt-2 flex flex-wrap gap-1">
                {ICONS.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    disabled={draft?.some((item) => item.id !== group.id && item.icon === icon)}
                    onClick={() =>
                      setDraft((current) => current?.map((item) => (item.id === group.id ? { ...item, icon } : item)) ?? current)
                    }
                    className={cn("rounded border px-2 py-1 text-xs", group.icon === icon && "border-brand-700")}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            ) : null}
          </section>
        ))
      )}
      {editing ? (
        <button
          type="button"
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          onClick={() =>
            setDraft((current) => [
              ...(current ?? []),
              { id: `new-${Date.now()}`, label: "New group", icon: ICONS.find((icon) => !current?.some((group) => group.icon === icon)) ?? "boxes", pagePaths: [] },
            ])
          }
        >
          Add group
        </button>
      ) : null}
    </div>
  );
}
